import { useEffect, useMemo } from 'react';

import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { useAeropayUser } from '@jane/bloom/hooks/bankLinking/useAeropayUser';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';

import { BloomBankLinkingModal } from './BloomBankLinkingModal';

interface BloomBankLinkingModalWithPromptProps {
  /** Persist the dismissal (14-day cookie) so we don't re-prompt on the next sign-in. */
  onDismiss: () => void;
  /** Unmount the prompt. */
  onRequestClose: () => void;
}

/**
 * Post-sign-in header prompt to link a bank account
 *
 * Loads the aeropay user to skip members who already have a linked bank; for
 * everyone else it opens `BloomBankLinkingModal` on the Jane Gold prompt screen.
 */
export const BloomBankLinkingModalWithPrompt = ({
  onDismiss,
  onRequestClose,
}: BloomBankLinkingModalWithPromptProps) => {
  const { appConfig } = useRootLoaderData();
  const { data, fetchAeropayUser, state } = useAeropayUser();

  useEffect(() => {
    if (data == null && state == 'idle') fetchAeropayUser();
  }, [data, state, fetchAeropayUser]);

  const isJaneGoldMember = useMemo(() => {
    if (!data) return false;
    const aeropayUser = data.success === true ? data.payload : null;
    return !!aeropayUser && aeropayUser.bank_accounts.length > 0;
  }, [data]);

  useEffect(() => {
    if (isJaneGoldMember) onRequestClose();
  }, [isJaneGoldMember, onRequestClose]);

  // fetcher.data is undefined until the load resolves — render nothing until
  // then so members with a linked bank never see the modal flash.
  if (data == null || isJaneGoldMember) return null;

  const handleClose = () => {
    onDismiss();
    onRequestClose();
  };

  return (
    <ClientOnly>
      {() => (
        <BloomBankLinkingModal
          aeropayConsumerId={appConfig.aeropay?.consumerId ?? ''}
          aeropayEnv={appConfig.aeropay?.env ?? ''}
          onRequestClose={handleClose}
          open
          showPrompt
          source="header"
        />
      )}
    </ClientOnly>
  );
};
